import { Box, Pane } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { sessionSummaryRequest } from '@fullstory/llm-tool-model';
import { SessionSummaryRequest } from '@fullstory/proto/fs/services/llm-tool';
import { timestamp } from '@fullstory/proto/google/protobuf';
import { useFlags, useNotifications, useXhr } from '@fullstory/react-hooks';
import { fullstorySelectors } from '@fullstory/redux';
import { Renderer } from '@fullstory/renderer-react';
import { useRequestQueryOptions } from '@fullstory/requests';
import { generateRoutePath, ORG_ROUTE, Playback } from '@fullstory/routes';
import { useObjectPreviewProps } from '@fullstory/signal-studio-context';
import { useQuery } from '@tanstack/react-query';
import { default as React, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { AISummaryPane } from '../AISummaryPane';
import { PlayOverlay } from './PlayOverlay';
import { createSessionSummaryCacheKey } from './utils';

const PLAYBACK_RANGE = 5000;
const PLAYBACK_SPEED = 50;
const SUMMARY_DELAY_MS = 500;
/**
 * Generate a session link from userId and sessionId
 */
export const generateSessionLink = (orgId: string, userId: string, sessionId: string) => {
  // Use the Playback function from routes to generate the route
  const route = Playback(userId, sessionId);

  // Generate the full URL
  return `${generateRoutePath(ORG_ROUTE, { orgId })}/${route.join('/')}`;
};

export const PreviewSession: React.FC<{
  userId: string;
  sessionId: string;
  pageId: string;
  pageOffset: number;
  timestamp: timestamp;
  /** URL to navigate to when play button is clicked */
  to: React.ComponentProps<typeof Link>['to'];
  /** Optional callback when play button is clicked */
  onPlayClick?: () => void;
  /** Optional flag to open in new tab */
  newTab?: boolean;
}> = ({ userId, sessionId, pageId, pageOffset, timestamp, to, onPlayClick, newTab }) => {
  const flags = useFlags();
  const tagsEnabled = flags.featureFlags().AppSummaryTags;
  const xhr = useXhr();
  const notifications = useNotifications();
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const ctx = useMemo(
    () => ({
      xhr: () => xhr,
      serverApiPrefix: () => '/',
      notifications: () => notifications,
      flags,
    }),
    [xhr, notifications, flags],
  );

  // Delay initial fetch to avoid unnecessary reqs when user briefly hovers over the session
  const [shouldFetch, setShouldFetch] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShouldFetch(true);
    }, SUMMARY_DELAY_MS);

    return () => clearTimeout(timeout);
  }, []);

  const { data, isPending, isError } = useQuery(
    useRequestQueryOptions(
      sessionSummaryRequest({
        userId,
        sessionId,
        orgId,
        timestamp,
        llmModel: SessionSummaryRequest.Model.V2_FLASH_FEW_SHOT,
        cacheOptions: null,
      }),
      {
        refetchOnMount: false,
        queryKey: createSessionSummaryCacheKey({
          orgId,
          userId,
          sessionId,
          timestamp,
        }),
        enabled: shouldFetch,
      },
    ),
  );

  const { data: dataTags } = useQuery(
    useRequestQueryOptions(
      sessionSummaryRequest({
        userId,
        sessionId,
        orgId,
        timestamp,
        llmModel: SessionSummaryRequest.Model.V0_TAGS,
        cacheOptions: null,
      }),
      {
        refetchOnMount: false,
        enabled: tagsEnabled && shouldFetch,
      },
    ),
  );

  const tags = useMemo(() => {
    if (!dataTags?.summary) {
      return [];
    }
    return dataTags.summary.split(',').map(tag => tag.trim());
  }, [dataTags]);

  const noop = useCallback(() => {}, []);

  const { results } = useObjectPreviewProps(userId, sessionId, pageId, pageOffset, noop);
  const startOffset =
    (results?.pageOffset || 0) > PLAYBACK_RANGE ? results!.pageOffset : PLAYBACK_RANGE;

  const [offset, setOffset] = useState(0);

  const handleLoad = () => {
    setOffset(PLAYBACK_RANGE * -1);

    const intervalId = setInterval(() => {
      setOffset(prevOffset => {
        const newOffset = prevOffset + PLAYBACK_SPEED;
        if (newOffset >= PLAYBACK_RANGE) {
          return PLAYBACK_RANGE * -1;
        }
        return newOffset;
      });
    }, PLAYBACK_SPEED);

    return () => clearInterval(intervalId);
  };

  const baseSessionLink = generateSessionLink(orgId, userId, sessionId);

  return (
    <Pane padding={16} borderRadius={4} minHeight={400} minWidth={500} border="standard">
      <Box
        testId="PreviewSession"
        data-component="PreviewSession"
        display="flex"
        flexDirection="column"
        gap={8}
        width="100%"
      >
        <Pane
          borderRadius={4}
          width="100%"
          height={300}
          style={{ position: 'relative', overflow: 'hidden' }}
        >
          <PlayOverlay
            to={{
              pathname: baseSessionLink,
            }}
            onClick={onPlayClick}
            newTab={newTab}
          >
            {results?.pageModel && (
              <Renderer
                ctx={ctx}
                orgId={orgId}
                onLoad={handleLoad}
                page={results.pageModel}
                offsetMs={startOffset + offset}
                key={`${userId}-${sessionId}-${pageId}-${results.pageOffset}`}
              />
            )}
          </PlayOverlay>
        </Pane>
      </Box>
      <Box marginTop={8} width="100%" maxWidth={500}>
        <AISummaryPane
          content={data?.summary}
          tags={tags}
          isLoading={isPending}
          jumpToMomentLink={to}
          error={
            isError
              ? intl.formatMessage({
                  defaultMessage: 'There was an error summarizing this session.',
                  id: 'C9WGkB',
                })
              : undefined
          }
          title={intl.formatMessage({
            defaultMessage: 'Relevant moment in the session',
            id: '7uDI6b',
          })}
          loadingMessage={intl.formatMessage({
            defaultMessage: 'Finding relevant moments',
            id: 'sqiXRL',
          })}
        />
      </Box>
    </Pane>
  );
};
